import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {UiManagerService} from "../../../services/ui-manager.service";
import {Personnel} from "../../../models/personnel";
import {PersonnelManagerService} from "../../../services/personnel-manager.service";
import {Skill, UserSkillBind} from "../../../models/skill";
import {SkillManagerService} from "../../../services/skill-manager.service";
import {Utils} from "../../../utils";
import {Role, RoleLocalization} from "../../../models/role";
import {Paginator} from "../../../models/paginator";
import {TableAction, TableColumn, TableColumnType} from "../../../components/table/table.component";
import {MatDialog} from "@angular/material/dialog";
import {
  BindSkillDialogComponent,
  BindSkillDialogData
} from "../../../components/dialogs/bind-skill-dialog/bind-skill-dialog.component";
import {AccountManagerService} from "../../../services/account-manager-service";
import {FieldService, UserFieldServiceAssignment} from '../../../models/field-service';
import {
  BindFieldServiceDialogComponent,
  BindFieldServiceDialogData
} from "../../../components/dialogs/bind-field-service-dialog/bind-field-service-dialog.component";
import {FieldServiceManagerService} from "../../../services/field-service-manager.service";
import {environment} from "@environments/environment";
import {DetailCardContent} from "../../../components/detail-card/detail-card.component";
import {TranslatePipe} from "@ngx-translate/core";

enum CreationMode {
  NONE, NEW, UPDATE
}

@Component({
  selector: 'app-create-personnel-page',
  templateUrl: './create-personnel-page.component.html',
  styleUrls: ['./create-personnel-page.component.scss']
})
export class CreatePersonnelPageComponent implements OnInit {

  isLoading = true;

  mode: CreationMode = CreationMode.NONE;
  person: Personnel;

  allRoles: Role[]
  currentRoles: Role[];
  activeRoles: Role[] = [];
  disabledRoles: Role[] = [];

  skills: UserSkillBind[];
  paginatorSkills: Paginator<UserSkillBind>;

  userFieldServicesAssignment: UserFieldServiceAssignment[];
  paginatorUserFieldServicesAssignment: Paginator<UserFieldServiceAssignment>;

  constructor(
    private personnelManagerService: PersonnelManagerService,
    private skillManagerService: SkillManagerService,
    private fieldServiceManagerService: FieldServiceManagerService,
    private uiManagerService: UiManagerService,
    private accountManagerService: AccountManagerService,
    public dialog: MatDialog,
    public route: ActivatedRoute,
    public router: Router,
    public translatePipe: TranslatePipe
  ) {
    this.paginatorSkills = new Paginator<UserSkillBind>();
    this.paginatorUserFieldServicesAssignment = new Paginator<FieldService>();
  }

  ngOnInit(): void {

    // Initialize new person
    this.person = new Personnel();

    this.route.paramMap.subscribe(async params => {

      const personId = params.get('id');

      if(personId) {

        // Fetch person
        this.person = await this.personnelManagerService.getPersonnel(personId);
        await this.fetchSkills();
        await this.fetchFieldServices();

        this.mode = CreationMode.UPDATE;

      } else {

        this.mode = CreationMode.NEW;
        this.skills = [];
        this.userFieldServicesAssignment = [];

      }

      await this.getRoles();

      this.isLoading = false;

    });

  }

  async fetchSkills() {
    this.paginatorSkills.onPageResults = (skills: UserSkillBind[]) => {
      this.skills = skills;
    }
    await this.skillManagerService.getSkillBinds(this.person, this.paginatorSkills);
  }

  async fetchFieldServices() {
    this.paginatorUserFieldServicesAssignment.onPageResults = (userFieldServiceAssignments: UserFieldServiceAssignment[]) => {
      this.userFieldServicesAssignment = userFieldServiceAssignments;
    }
    await this.accountManagerService.getFieldServiceBindings(this.person.id, this.paginatorUserFieldServicesAssignment);
  }

  async getRoles() {

    // Get all roles
    this.allRoles = await this.accountManagerService.getAllRoles();

    // Get active roles
    if (this.person.id) {
      this.currentRoles = await this.accountManagerService.getRoles(this.person.id);
    } else {
      this.currentRoles = [];
    }
    this.activeRoles = this.currentRoles.slice(0);
    this.disabledRoles = [];
    this.checkDisabledRoles();

  }

  getCardTitle() {
    switch (this.mode) {
      case CreationMode.NONE:
        return '&nbsp;';
      case CreationMode.NEW:
        return this.translatePipe.transform('Create personnel');
      case CreationMode.UPDATE:
        return `${this.translatePipe.transform('Manage personnel')} ${this.person.name}`;
    }
  }

  async save() {

    // Check validation
    if (!this.person.validate()) {
      return;
    }

    // Loading spinner on
    this.isLoading = true;

    // Save personnel
    switch (this.mode) {

      case CreationMode.NEW:
        this.person = await this.personnelManagerService.createPersonnel(this.person);
        await this.router.navigate(['personnel', this.person.id], { replaceUrl: true });
        break;

      case CreationMode.UPDATE:
        await this.personnelManagerService.updatePersonnel(this.person.id, this.person);
        await this.router.navigate(['personnel']);
        break;
    }

    // Save roles
    let rolesToAdd = this.activeRoles.filter(x => !this.currentRoles.includes(x));
    let rolesToDelete = this.currentRoles.filter(x => !this.activeRoles.includes(x));
    rolesToAdd.forEach(role => this.accountManagerService.addRole(this.person.id, role));
    rolesToDelete.forEach(role => this.accountManagerService.removeRole(this.person.id, role));

    // Loading spinner off
    this.isLoading = false;

  }

  async delete() {
    this.isLoading = true;

    await this.uiManagerService.showConfirmDialog('Remove personnel?', 'Are you sure do you want to remove this personnel?\nThis operation is irreversible.', async () => {
      await this.personnelManagerService.removePersonnel(this.person.id);
      await this.router.navigate(['personnel']);
    });

    this.isLoading = false;
  }

  getModes() {
    return CreationMode;
  }

  updateRole(role: Role, enabled: boolean) {
    if (enabled && !this.activeRoles.includes(role)) {
      this.activeRoles.push(role);
    } else {
      this.activeRoles.splice(this.activeRoles.indexOf(role), 1);
    }

    // If company admin is enable -> enable expert too
    if (this.activeRoles.includes(Role.ROLE_ADMIN) && !this.activeRoles.includes(Role.ROLE_EXPERT)) {
      this.activeRoles.push(Role.ROLE_EXPERT);
    }

    this.checkDisabledRoles();

  }

  checkDisabledRoles() {
    if (this.activeRoles.includes(Role.ROLE_ADMIN)) {
      if (!this.disabledRoles.includes(Role.ROLE_EXPERT)) {
        this.disabledRoles.push(Role.ROLE_EXPERT);
      }
    } else {
      this.disabledRoles.splice(this.disabledRoles.indexOf(Role.ROLE_EXPERT), 1);
    }
  }

  bindNewSkill() {
    const dialogRef = this.dialog.open(BindSkillDialogComponent, {
      width: '400px',
      data: {
        isNew: true,
        assignedSkills: this.skills.map(skill => skill.skill)
      } as BindSkillDialogData
    });

    dialogRef.afterClosed().subscribe(async (bindingData: BindSkillDialogData) => {
      if (bindingData) {
        await this.skillManagerService.bindSkill(this.person, bindingData.skill, bindingData.skillLevel);
        await this.paginatorSkills.refresh();
      }
    });
  }

  getSkillsTableColumns(): TableColumn[] {
    return [
      { key: 'skill', type: TableColumnType.DEFAULT, title: 'Skill', isMasterKey: true },
      { key: 'skillLevel', type: TableColumnType.DEFAULT, title: 'Level' },
      { key: 'startAt', type: TableColumnType.DATE, title: 'Start at' },
      { key: 'expiringAt', type: TableColumnType.DATE, title: 'Expiring at' },
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getSkillsTableData() {
    return this.skills?.map(skillBinding => {
      return {
        id: skillBinding.skill.id,
        skill: skillBinding.skill.name,
        skillLevel: skillBinding.skillLevel.name,
        startAt: new Date(skillBinding.startAt),
        expiringAt: new Date(skillBinding.expiringAt),
        actions: [
          {
            icon: 'edit', callback: () => {

              const dialogRef = this.dialog.open(BindSkillDialogComponent, {
                width: '400px',
                data: {
                  skillId: skillBinding.skill.id,
                  skillLevelId: skillBinding.skillLevel.id,
                  assignedSkills: this.skills.map(skill => skill.skill)
                } as BindSkillDialogData
              });

              dialogRef.afterClosed().subscribe(async (skillData: BindSkillDialogData) => {
                if (skillData) {
                  await this.skillManagerService.updateSkillBinding(this.person, skillData.skill, skillData.skillLevel);
                  await this.paginatorSkills.refresh();
                }
              });

            }
          },
          {
            icon: 'delete', color: 'warn', callback: () => {

              this.uiManagerService.showConfirmDialog('Remove skill', 'Are you sure do you want to remove this skill binding?<br>This operation is irreversible.', async () => {
                await this.skillManagerService.removeSkillBinding(this.person, skillBinding.skill.id);
                await this.paginatorSkills.refresh();
              });

            }
          }
        ] as TableAction[],

      };
    }) || [];
  }

  bindNewFieldService() {
    const dialogRef = this.dialog.open(BindFieldServiceDialogComponent, {
      width: '400px',
      data: {
        isNew: true,
        userFieldServiceAssignments: this.userFieldServicesAssignment
      } as BindFieldServiceDialogData
    });

    dialogRef.afterClosed().subscribe(async (bindingData: BindFieldServiceDialogData) => {
      if (bindingData) {
        await this.fieldServiceManagerService.addFieldServiceBind(bindingData.currentUserFieldServiceAssignment.fieldService, this.person, bindingData.currentUserFieldServiceAssignment.isManager);
        await this.paginatorUserFieldServicesAssignment.refresh();
      }
    });
  }

  getFieldServicesTableColumns(): TableColumn[] {
    return [
      { key: 'name', type: TableColumnType.DEFAULT, title: 'Name', isMasterKey: true },
      { key: 'address', type: TableColumnType.DEFAULT, title: 'Address' },
      { key: 'city', type: TableColumnType.DEFAULT, title: 'City' },
      { key: 'zip', type: TableColumnType.DEFAULT, title: 'Zip' },
      { key: 'province', type: TableColumnType.DEFAULT, title: 'Province' },
      { key: 'country', type: TableColumnType.DEFAULT, title: 'Country' },
      environment.features.fieldManagerManagement ? { key: 'isManager', type: TableColumnType.BOOLEAN, title: 'Field service manager' } : null,
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ].filter(x => x != null);
  }

  getFieldServicesTableData() {
    return this.userFieldServicesAssignment?.map(userFieldServiceAssignment => {
      return {
        id: userFieldServiceAssignment.fieldService.id,
        name: userFieldServiceAssignment.fieldService.name,
        address: userFieldServiceAssignment.fieldService.location?.address,
        city: userFieldServiceAssignment.fieldService.location?.city,
        zip: userFieldServiceAssignment.fieldService.location?.zip,
        province: userFieldServiceAssignment.fieldService.location?.provinceInitials,
        country: userFieldServiceAssignment.fieldService.location?.country,
        isManager: userFieldServiceAssignment.isManager,
        actions: [
          {
            icon: 'delete', color: 'warn', callback: () => {

              this.uiManagerService.showConfirmDialog('Remove field service binding', 'Are you sure do you want to remove this field service binding?<br>This operation is irreversible.', async () => {
                await this.fieldServiceManagerService.removeFieldServiceBind(userFieldServiceAssignment.fieldService, this.person);
                await this.paginatorUserFieldServicesAssignment.refresh();
              });

            }
          }
        ] as TableAction[],

      };
    }) || [];
  }

  filterFieldService(filter: string) {
    this.uiManagerService.notify(this.translatePipe.transform('Not implemented'));
  }

  showFieldService(fieldServiceId: string) {
    this.router.navigate(['field-service', fieldServiceId]);
  }

  showSkill(skillId: string) {
    this.router.navigate(['skill']);
  }

  get utils() { return Utils }

  get RoleLocalization() { return RoleLocalization; }

  get CreationMode() { return CreationMode }

  get DetailCardContent() { return DetailCardContent; }

  get environment() { return environment }

}
